import { useEffect } from 'react';
import { AuthStatus, useAuth } from './auth/Auth';
import { useApi } from './api/Api';
import { useCache } from './api/cache/Cache';
import { UserSummary } from './database/user';

/**
 * AppDataLoader fetches and caches global data when the user is authenticated.
 * It must be rendered inside AuthProvider, ApiProvider, and CacheProvider.
 */
function AppDataLoader() {
    const auth = useAuth();
    const api = useApi();
    const cache = useCache();

    useEffect(() => {
        if (auth.status !== AuthStatus.Authenticated) {
            return;
        }

        if (cache.userSummaries.request.isSent()) {
            return;
        }
        cache.userSummaries.request.onStart();

        const fetchAllUsers = async () => {
            const allSummaries: UserSummary[] = [];
            let startKey: string | undefined;

            do {
                const response = await api.listUsersPublic(startKey);
                const summaries: UserSummary[] = response.data.users.map((user) => ({
                    username: user.username,
                    displayName: user.displayName,
                    isArtist: user.isArtist,
                    subscriptionStatus: user.subscriptionStatus,
                    subscriptionOverride: user.subscriptionOverride,
                    quote: user.quote,
                }));
                allSummaries.push(...summaries);
                startKey = response.data.lastEvaluatedKey;
            } while (startKey);

            return allSummaries;
        };

        fetchAllUsers()
            .then((summaries) => {
                cache.userSummaries.putMany(summaries);
                cache.userSummaries.request.onSuccess();
            })
            .catch((err) => {
                console.error('Failed to load user summaries:', err);
                cache.userSummaries.request.onFailure(err);
            });
    }, [auth.status, api, cache.userSummaries]);

    return null;
}

export default AppDataLoader;
